export const VOLUME_NAME_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;

export type VolumeScope = "branch" | "run";
export type VolumeAccess = "rw" | "ro";
export type VolumeConcurrency = "shared" | "exclusive";

export type VolumeDirective = {
  name: string;
  scope: VolumeScope;
  access: VolumeAccess;
  concurrency: VolumeConcurrency;
};

export type VolumeRefKind = "branch" | "run" | "user";

export type VolumeMount = VolumeDirective & {
  namespace: string;
  volumeId: string;
  refKind: VolumeRefKind;
  refId: string;
};

export function volumeIsReadOnly(volume: VolumeDirective): boolean {
  return volume.access === "ro";
}

export function volumeRequiresExclusiveClaim(volume: VolumeDirective): boolean {
  return volume.access === "rw" && volume.concurrency === "exclusive";
}

export type VolumeIdentity = {
  name: string;
  namespace: string;
  volumeId: string;
  refKind: VolumeRefKind;
  refId: string;
};

export type VolumeEntry = {
  name: string;
  kind: "file" | "directory" | "symlink";
  size: number;
  modifiedAt: number;
};

export type VolumeUsage = {
  logicalBytes: number;
};

export type ListOptions = {
  path?: string;
  cursor?: string;
  limit?: number;
};

export type ListResult = {
  entries: VolumeEntry[];
  nextCursor?: string;
};

export type VolumeMountSpec = {
  source: string;
  target: string;
  readOnly: boolean;
};

export type ReadResult = {
  body: Uint8Array<ArrayBuffer>;
  size: number;
  range?: {
    start: number;
    end: number;
    total: number;
  };
};

export type ReadOptions = {
  rangeHeader?: string | null;
};

export class VolumeFileNotFound extends Error {
  constructor(path: string) {
    super(`File not found: ${path}`);
    this.name = "VolumeFileNotFound";
  }
}

export class VolumeFileTooLarge extends Error {
  constructor(path: string) {
    super(`File is too large to read: ${path}`);
    this.name = "VolumeFileTooLarge";
  }
}

export class VolumeNotAFile extends Error {
  constructor(path: string) {
    super(`Path is not a file: ${path}`);
    this.name = "VolumeNotAFile";
  }
}

export class VolumeRangeNotSatisfiable extends Error {
  constructor(readonly contentRange?: string) {
    super("Range not satisfiable");
    this.name = "VolumeRangeNotSatisfiable";
  }
}

export interface VolumeBackend {
  readonly kind: "nest";

  ensure(id: VolumeIdentity): Promise<void>;
  fork(source: VolumeIdentity, destination: VolumeIdentity): Promise<void>;
  list(id: VolumeIdentity, opts?: ListOptions): Promise<ListResult>;
  usage(id: VolumeIdentity): Promise<VolumeUsage>;
  read(
    id: VolumeIdentity,
    path: string,
    opts?: ReadOptions
  ): Promise<ReadResult>;
  remove(id: VolumeIdentity): Promise<void>;

  mountSpec(
    id: VolumeIdentity,
    readOnly: boolean,
    opts?: unknown
  ): VolumeMountSpec;
}
